import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { from } from 'rxjs';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { AuthService } from '@/core/auth/auth.service';
import { CertidaoAlteracaoContratualPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/certidao-alteracao-contratual-pj/certidao-alteracao-contratual-pj.service';
import { ReportCertidaoAlteracaoContratualPjRequest, ReportCertidaoAlteracaoContratualPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/certidao-alteracao-contratual-pj/certidao-alteracao-contratual-pj.model';
import { CertidaoAlteracaoContratualPjJobService } from '@/core/services/certidao-alteracao-contratual-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportCertidaoAlteracaoContratualPjRequest;
type Resp = ReportCertidaoAlteracaoContratualPjResponse;

@Component({
    selector: 'app-certidao-alteracao-contratual-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './certidao-alteracao-contratual-pj.html',
    styleUrl: './certidao-alteracao-contratual-pj.scss'
})
/**
 * Relatório PDF assíncrono: Certidão de Alteração Contratual (prestador PJ).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`) e o emissor (usuário logado, via `AuthService`).
 */
export class ReportCertidaoAlteracaoContratualPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly certidaoService = inject(CertidaoAlteracaoContratualPjService);
    private readonly authService = inject(AuthService);
    private readonly pessoaService = inject(PessoaService);
    readonly jobService = inject(CertidaoAlteracaoContratualPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    /** Nome do usuário logado, resolvido de forma assíncrona; impresso como emissor no rodapé. */
    private usuarioNome = '';

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Restaura o job da sessão (base) e carrega o nome do usuário logado para o emissor. */
    override ngOnInit(): void {
        super.ngOnInit();
        this.inicializarUsuarioLogado();
    }

    private inicializarUsuarioLogado(): void {
        from(this.authService.getUserInfo()).subscribe((userInfo) => {
            this.usuarioNome = userInfo.name || '';
        });
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a certidão.'
            });
            return null;
        }

        const claim = this.authService.identityClaims?.['preferred_username'];
        const emissor = this.usuarioNome || (typeof claim === 'string' ? claim : '') || 'Sistema';

        return { nuCrm, sgUf, emissor };
    }

    protected exportar(request: Req) {
        return this.certidaoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
